/**
 * Bare-bones controlled tabs. We avoid Radix to keep deps tight; the dashboard
 * doesn't need keyboard-arrow nav across tab triggers (yet).
 */
import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface TabsProps<T extends string> {
  tabs: { value: T; label: string; count?: number }[];
  value: T;
  onChange: (value: T) => void;
  children?: ReactNode;
  className?: string;
}

export function Tabs<T extends string>({ tabs, value, onChange, children, className }: TabsProps<T>) {
  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center gap-1 border-b border-border">
        {tabs.map((tab) => {
          const active = tab.value === value;
          return (
            <button
              key={tab.value}
              onClick={() => onChange(tab.value)}
              className={cn(
                'relative inline-flex items-center gap-1.5 px-3 py-2 text-sm transition-colors',
                active
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="rounded-md bg-muted px-1.5 text-[11px] font-medium text-muted-foreground">
                  {tab.count}
                </span>
              )}
              {active && <span className="absolute inset-x-0 -bottom-px h-px bg-foreground" />}
            </button>
          );
        })}
      </div>
      <div>{children}</div>
    </div>
  );
}
